<template>
  <div class="algolia-search">
    <!-- Using v-autocomplete with the multiple prop as a workaround for keeping the search results open after
     selection. -->
    <v-autocomplete
      ref="autocompleteRef"
      v-model="selectedResults"
      v-model:search="searchInput"
      data-testid="search-results"
      :items="hits"
      :item-title="item => item.Name"
      :item-value="item => item"
      no-filter
      :placeholder="placeholderText"
      hide-details
      clearable
      density="compact"
      variant="outlined"
      menu-icon=""
      append-inner-icon="mdi-magnify"
      multiple
      :menu-props="{ ref: 'menuRef', maxWidth: `${searchElementWidth}` }"
      class="algolia-search__autocomplete"
      @update:search="onSearchUpdate"
      @update:model-value="onSelection"
      @blur="onBlur"
    >
      <template #item="{ item, props }">
        <v-list-item
          v-bind="props"
          :id="`search-result-${item.title}`"
          :data-testid="`search-result-${item.raw.Name}`"
          class="algolia-search__result"
          :title="item.raw.Name"
        >
          <template #prepend>
            <v-avatar
              v-if="getIconUrlForHit(item.raw)"
              size="20"
            >
              <img
                :src="getIconUrlForHit(item.raw)"
                class="algolia-search__result-icon"
              >
            </v-avatar>
          </template>
          <template #title>
            <span
              class="algolia-search__result-text"
            >{{ item.raw.Name }}</span>
          </template>
        </v-list-item>
      </template>
    </v-autocomplete>
  </div>
</template>

<script lang="ts">
// Types
import type {Hit} from 'instantsearch.js/es/types';
import type {InstantSearch} from 'instantsearch.js';
import type {PropType} from 'vue';

// 3rd Party Libraries
import algoliasearch from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import {connectHits, connectConfigure} from 'instantsearch.js/es/connectors';

// Services
import {wrapError} from '@northpoint-development/labs-common-core';
import {TokenService} from '@/services/TokenService';

export interface AlgoliaHit {
  Name: string;
  location: {
    _latitude: number;
    _longitude: number;
  };
  metadata?: {
    Address: string;
    City: string;
    State: string;
    Developer: string;
  };
  objectID: string;
}

export default {
  name: 'AlgoliaAutocomplete',
  props: {
    placeholderText: {
      type: String,
      required: true,
    },
    iconUrl: {
      type: String,
      required: false,
      default: '',
    },
    getItemIconUrl: {
      type: Function as PropType<(hit: AlgoliaHit) => string | undefined>,
      required: false,
      default: undefined,
    },
    algoliaIndexName: {
      type: String,
      required: true,
    },
    algoliaHits: {
      type: Array<any>,
      default: () => [],
    },
    searchElementWidth: {
      type: String,
      default: '373px',
    },
  },
  emits: ['select', 'stop-searching'],
  data() {
    return {
      searchInput: '',
      hits: [] as Hit[],
      search: null as InstantSearch | null,
      selectedResults: [] as AlgoliaHit[],
      configureWidget: null as ReturnType<typeof connectConfigure> | null,
    };
  },
  computed: {
    excludeFilters() {
      if (!this.algoliaHits || !this.algoliaHits.length) return '';

      // Create a filter string to exclude already selected items from the search results
      const filterString = this.algoliaHits
        .map((hit) => hit.id)
        .filter(Boolean)
        .map((id) => `NOT objectID:${id}`)
        .join(' AND ');

      return filterString;
    },
  },
  watch: {
    algoliaHits: {
      handler() {
        this.updateFilters();
        // A new link pushes the search down, and the menu only follows a resize or scroll.
        this.$refs.autocompleteRef?.$refs.menuRef?.updateLocation();
      },
      deep: true,
      flush: 'post',
    },
  },
  async mounted() {
    const apiKey = await TokenService.getToken('algoliaKey');
    if (!apiKey) {
      console.error('Algolia API key is not set');
      return;
    }
    this.search = instantsearch({
      indexName: this.algoliaIndexName,
      searchClient: algoliasearch('1TIGF75UT0', apiKey),
      future: {
        preserveSharedStateOnUnmount: true,
      },
    });

    const hitsWidget = connectHits((renderOptions) => this.hits = renderOptions.hits);
    const configureWidget = connectConfigure(() => {});

    this.search.addWidgets([
      hitsWidget({}),
      configureWidget({
        searchParameters: {
          filters: this.excludeFilters,
        },
      }),
    ]);

    this.search.start();

    // Focus the input after component mounts
    this.$nextTick(() => {
      (this.$refs.autocompleteRef as HTMLElement)?.focus();
    });
  },
  beforeUnmount() {
    if (this.search) {
      this.search.dispose();
      this.search = null;
    }
  },
  methods: {
    getIconUrlForHit(hit: AlgoliaHit): string {
      return this.getItemIconUrl?.(hit) || this.iconUrl;
    },
    onBlur(event: FocusEvent) {
      // Navigating through search results with keyboard should not trigger stop-searching
      if (event?.relatedTarget?.id?.toString()?.startsWith('search-result-')) return;
      this.$emit('stop-searching');
    },
    updateFilters() {
      this.search!.helper?.setQueryParameter('filters', this.excludeFilters).search();
    },
    onSearchUpdate(value: string | null) {
      if (this.search) {
        try {
          this.search.helper?.setQuery(value ?? '').search();
        } catch (error) {
          console.error(wrapError(error, 'Error during Algolia search update'));
        }
      }
    },
    onSelection() {
      if (!this.selectedResults || !this.selectedResults.length) return;

      if (this.selectedResults[0]) {
        // No re-search: the linked record drops out of the results when algoliaHits updates.
        this.$emit('select', this.selectedResults[0]);
        // Clear the search results so that they no longer appear in the input
        this.selectedResults = [];
      }
    },
  },
};
</script>

<style scoped lang="scss">
:deep(.v-list-item-title) {
  @extend .display-field-text;
}

:deep(.v-list-item__prepend) {
  margin-right: -8px;
}

.algolia-search {
  width: 100%;
  max-width: 400px;
  @extend .display-field-text;

  &_autocomplete {
    min-width: v-bind(searchElementWidth);
    max-width: v-bind(searchElementWidth);
  }

  &__result {
    height: 28px;
    min-height: 28px;
    padding-left: 8px !important;
    --v-border-color: #ebebeb;
    --v-border-opacity: 0;
    &:hover {
      background-color: rgba(235, 235, 235, 1);
    }
    &:focus-visible{
      background-color: rgba(235, 235, 235, 0.55);
      &::after {
        opacity: 0;
      }
    }
    &-icon {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
    &-text {
      @extend .display-field-text;
    }
  }

  /* Make autocomplete compact and match display-field-text styling */
  :deep(.v-field) {
    min-height: 28px !important;
  }

  :deep(.v-field__input) {
    min-height: 28px !important;
    padding: 4px 4px 4px 12px;
  }

  :deep(.v-field__input input) {
    @extend .display-field-text;
  }

  :deep(.v-field__clearable i) {
    font-size: 22px;
  }
  :deep(.v-field__append-inner i) {
    font-size: 22px;
  }
}
</style>
